'use client';

import { useState } from 'react';
import { CheckIcon } from './Icons';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3028/api';

const PRODUCT_CONFIG = {
  clinic: {
    orgLabel: 'Nome dello studio',
    orgPlaceholder: 'Studio Medico Rossi',
    orgField: 'studioName',
    accountType: 'studio_medico',
  },
  safe: {
    orgLabel: 'Nome azienda',
    orgPlaceholder: 'Azienda S.r.l.',
    orgField: 'companyName',
    accountType: 'azienda_safe',
  },
};

export default function RegisterForm({ product = 'clinic' }) {
  const config = PRODUCT_CONFIG[product];

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    orgName: '',
    privacyAccepted: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  function validate() {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = true;
    if (!form.lastName.trim()) newErrors.lastName = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = true;
    if (!form.password || form.password.length < 8) newErrors.password = true;
    if (!form.orgName.trim()) newErrors.orgName = true;
    if (!form.privacyAccepted) newErrors.privacyAccepted = true;
    return newErrors;
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => { const next = { ...prev }; delete next[name]; return next; });
    }
    if (apiError) setApiError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setLoading(true);
    setApiError('');

    try {
      const res = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          [config.orgField]: form.orgName.trim(),
          accountType: config.accountType,
          privacyAccepted: form.privacyAccepted,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 429) {
          setApiError('Troppe richieste. Riprova tra un\'ora.');
        } else if (res.status === 409) {
          setApiError('Questa email è già registrata. Prova ad accedere.');
        } else if (data?.error?.message) {
          setApiError(data.error.message);
        } else {
          setApiError('Si è verificato un errore. Riprova più tardi.');
        }
        return;
      }

      setSubmitted(true);
    } catch {
      setApiError('Impossibile completare la registrazione. Verifica la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="form-card">
        <div className="form-success">
          <div className="check-icon"><CheckIcon size={14} color="#fff"/></div>
          <div>
            <h4>Controlla la tua email</h4>
            <p>
              Ti abbiamo inviato un link di verifica all&apos;indirizzo <strong>{form.email}</strong>.
              Clicca il link per attivare il tuo account e iniziare ad usare SOIIA.
            </p>
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 16, marginBottom: 0 }}>
          Non hai ricevuto l&apos;email? Controlla la cartella spam oppure <a href={`/${product}/registrati`} style={{ color: 'var(--text)', fontWeight: 500 }}>riprova</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="form-card">
      {apiError && (
        <div className="form-error" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#DC2626', fontSize: 14 }}>
          {apiError}
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row-grid">
          <div className="form-row">
            <label>Nome <span className="req">*</span></label>
            <input
              type="text"
              name="firstName"
              placeholder="Mario"
              value={form.firstName}
              onChange={handleChange}
              disabled={loading}
              style={errors.firstName ? { borderColor: '#ef4444' } : undefined}
            />
          </div>
          <div className="form-row">
            <label>Cognome <span className="req">*</span></label>
            <input
              type="text"
              name="lastName"
              placeholder="Rossi"
              value={form.lastName}
              onChange={handleChange}
              disabled={loading}
              style={errors.lastName ? { borderColor: '#ef4444' } : undefined}
            />
          </div>
        </div>
        <div className="form-row">
          <label>Email <span className="req">*</span></label>
          <input
            type="email"
            name="email"
            placeholder={product === 'clinic' ? 'mario@studiomedico.it' : 'mario@azienda.it'}
            value={form.email}
            onChange={handleChange}
            disabled={loading}
            style={errors.email ? { borderColor: '#ef4444' } : undefined}
          />
        </div>
        <div className="form-row">
          <label>Password <span className="req">*</span></label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Minimo 8 caratteri"
              value={form.password}
              onChange={handleChange}
              disabled={loading}
              style={{
                ...(errors.password ? { borderColor: '#ef4444' } : {}),
                paddingRight: 44,
                width: '100%',
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle"
              aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/><path d="M3 3l18 18"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
        </div>
        <div className="form-row">
          <label>{config.orgLabel} <span className="req">*</span></label>
          <input
            type="text"
            name="orgName"
            placeholder={config.orgPlaceholder}
            value={form.orgName}
            onChange={handleChange}
            disabled={loading}
            style={errors.orgName ? { borderColor: '#ef4444' } : undefined}
          />
        </div>
        <div className="form-row" style={{ marginBottom: 24 }}>
          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="privacyAccepted"
              checked={form.privacyAccepted}
              onChange={handleChange}
              disabled={loading}
              style={{ marginTop: 3, accentColor: '#000', width: 16, height: 16 }}
            />
            <span style={{ fontSize: 13, color: errors.privacyAccepted ? '#ef4444' : 'var(--text-3)', lineHeight: 1.4 }}>
              Ho letto e accetto la <a href="/other/privacy-policy" target="_blank" style={{ color: 'var(--text)', fontWeight: 500 }}>Privacy Policy</a> e i <a href="/other/terms-condition" target="_blank" style={{ color: 'var(--text)', fontWeight: 500 }}>Termini di Servizio</a>
            </span>
          </label>
        </div>
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading}>
          {loading ? 'Registrazione in corso...' : 'Crea il mio account'}
        </button>
        <p style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 16, textAlign: 'center', marginBottom: 0 }}>
          Hai già un account? <a href="https://soiia.mowki.com/auth/sign-in" style={{ color: 'var(--text)', fontWeight: 500 }}>Accedi</a>
        </p>
      </form>
    </div>
  );
}
